import * as React from 'react'
import MediaQuery from 'react-responsive'
import { noop } from 'lodash'
import { connect } from 'react-redux'
import {
  Text,
  StackChildren,
  InlineChildren,
  Breakpoints,
  Colors,
} from '@doordash/design-language'
import { RTFPrivacyFlow } from '../../../models/types'
import Button from '@doordash/component-button'
import * as events from '../../../events'
import * as DA from '../../../typeModules/events'
import {
  ANDROID_CANCEL_DEEPLINK,
  IOS_CANCEL_DEEPLINK,
} from '../../pages/privacy-portal'
import ResponsiveWrapper from '../../atoms/responsive-wrapper'
import s from './strings'
import { OsTypes } from '../../../webview/types'
import { isWebview, detectWebviewOs } from '../../../utilities/userAgent'
import { Interpolate } from '@doordash/lib-i18n'
import i18n from '../../../i18n'
import { i18nKeys } from 'translationTypes'

const PAGE_NAME = 'request-deletion'

type Props = {
  setView?: (view: RTFPrivacyFlow) => void
  trackRTF?: (data: DA.RTF) => void
  handlePrivacyPolicyClick?: () => void
}

export const RequestDeletion: React.FC<Props> = ({
  setView = noop,
  trackRTF = noop,
  handlePrivacyPolicyClick = noop,
}) => {
  React.useEffect(() => {
    trackRTF({ action: DA.GDPRActions.Render, name: PAGE_NAME })
  }, [trackRTF])

  const os = detectWebviewOs()

  const handleContinueClick = () => {
    trackRTF({ action: DA.GDPRActions.PreSubmission, name: PAGE_NAME })
    setView(RTFPrivacyFlow.Confirm)
  }

  const handleCancelClick = () => {
    trackRTF({ action: DA.GDPRActions.Close, name: PAGE_NAME })
    if (isWebview()) {
      window.location.assign(
        os === OsTypes.iOS ? IOS_CANCEL_DEEPLINK : ANDROID_CANCEL_DEEPLINK
      )
    } else {
      window.location.reload()
    }
  }

  return (
    <ResponsiveWrapper>
      <StackChildren size={StackChildren.Sizes.Large}>
        <Text styles={Text.Styles.PageTitle2}>
          {s.requestDeletion.pageTitle()}
        </Text>
        <Text styles={Text.Styles.Body1}>{s.requestDeletion.body1()}</Text>
        <Text styles={Text.Styles.Body1}>{s.requestDeletion.body2()}</Text>
        <Text styles={Text.Styles.Body1}>{s.requestDeletion.body3()}</Text>
        <Text styles={Text.Styles.Body1}>
          <Interpolate
            i18n={i18n}
            i18nKey={i18nKeys.privacy_requestDeletion_body4_new}
            components={[
              /* eslint-disable-next-line jsx-a11y/anchor-has-content */
              <a
                role="link"
                style={{ color: Colors.TextAction }}
                onClick={handlePrivacyPolicyClick}
              >
                ##Privacy Policy##
              </a>,
            ]}
          />
        </Text>
        <MediaQuery minWidth={Breakpoints.Mobile + 1}>
          <InlineChildren
            size={InlineChildren.Sizes.XxSmall}
            justifyContent={InlineChildren.Justification.FlexEnd}
          >
            <Button
              onClick={handleCancelClick}
              type={Button.Types.Tertiary}
              isInline
            >
              {s.requestDeletion.cancelButton()}
            </Button>
            <Button onClick={handleContinueClick} isInline>
              {s.requestDeletion.continueButton()}
            </Button>
          </InlineChildren>
        </MediaQuery>
      </StackChildren>
      <MediaQuery maxWidth={Breakpoints.Mobile}>
        <StackChildren size={StackChildren.Sizes.Small}>
          <Button onClick={handleContinueClick}>
            {s.requestDeletion.continueButton()}
          </Button>
          <Button onClick={handleCancelClick} type={Button.Types.FlatSecondary}>
            {s.requestDeletion.cancelButton()}
          </Button>
        </StackChildren>
      </MediaQuery>
    </ResponsiveWrapper>
  )
}

const mapDispatchToProps = {
  trackRTF: events.trackRTF,
}

export default connect(null, mapDispatchToProps)(RequestDeletion)
